/**
 * PlusReportBuyCard — the actual monetised offering embedded at the
 * top of `/plus`. Ships a $49 one-off "Personalised Report" checkout
 * button that opens Stripe hosted checkout. Signed-out users see a
 * sign-in prompt. On click we POST /wua-plus/checkout, receive a
 * `checkout_url`, and redirect the browser to Stripe.
 *
 * On success Stripe redirects to /wua-plus/success?session_id=...
 * which polls the backend, triggers report generation, then routes
 * the buyer to /wua-plus/report/{report_id}.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, ShieldCheck, ArrowRight, Loader2, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function PlusReportBuyCard() {
  const { user, login } = useAuth();
  const [busy, setBusy] = useState(false);

  const startCheckout = async () => {
    if (!user) { login(); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/wua-plus/checkout", {
        origin_url: window.location.origin,
      });
      // Full-page redirect — never navigate() to a Stripe URL through
      // the SPA router.
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Could not start checkout — please try again in a moment."
      );
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="plus-report-buy-card"
      className="max-w-7xl mx-auto px-6 lg:px-10 pt-10"
    >
      <div className="rounded-xl border-2 border-[#4C1D95] bg-white p-6 sm:p-8 shadow-sm">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-[11px] font-underground-sc tracking-wider text-emerald-800">
              <Sparkles className="h-3 w-3" />
              New · Instant · Pre-WUA+
            </div>
            <h2 className="mt-3 font-editorial text-3xl sm:text-4xl text-[#002147] leading-tight">
              WUA Personalised Report — $49 one-off
            </h2>
            <p className="mt-1 text-[12px] font-underground-sc tracking-wider text-stone-500">
              Step 1 · Get the report. Step 2 (optional) · Talk 1:1 with a WUA+ advisor.
            </p>
            <p className="mt-3 text-stone-700 leading-relaxed">
              We feed your profile + shortlist into Claude Sonnet 5 under an
              editorial prompt written by the WUA newsroom. In under 60
              seconds you get a strategic, sourced, five-section report:
              portfolio balance, financial reality check, visa preparation
              order, application timeline, and an editor's verdict. Same
              methodology a $500/hr consultant would use — printed while
              you make a coffee.
            </p>
            <ul className="mt-4 space-y-1.5 text-[13px] text-stone-700">
              <li>✓ One flat $49. No subscription. No upsell.</li>
              <li>✓ Uses only your data — never invents rankings or alumni.</li>
              <li>✓ Downloadable + emailed. Yours forever.</li>
              <li>✓ Refunded in full if you're not happy in the first 48 hours.</li>
            </ul>
            <p
              data-testid="plus-report-upgrade-note"
              className="mt-4 text-[12px] text-stone-600 border-l-2 border-[#4C1D95]/30 pl-3 italic"
            >
              Want to talk the nuance through with a real person? After the
              report lands you can book a 1:1 session with a{" "}
              <Link to="/wua-plus" className="not-italic text-[#4C1D95] hover:underline font-medium">
                WUA+ professional advisor
              </Link>
              {" "}— a separate premium service for families who want a human on the line.
            </p>
          </div>
          <div className="text-center md:text-right">
            <Button
              data-testid="plus-buy-report-btn"
              onClick={startCheckout}
              disabled={busy}
              size="lg"
              className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md w-full md:w-auto"
            >
              {busy ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
              ) : (
                <>Buy report — $49 <ArrowRight className="h-4 w-4 ml-2" /></>
              )}
            </Button>
            <div className="mt-2">
              <Link
                to="/wua-plus/sample"
                data-testid="plus-see-sample-link"
                className="inline-flex items-center gap-1 text-[12px] text-[#4C1D95] hover:underline"
              >
                <FileText className="h-3 w-3" />
                See a sample report first
              </Link>
            </div>
            <div className="mt-3 flex items-center justify-center md:justify-end gap-1.5 text-[11px] font-underground-sc tracking-wider text-stone-500">
              <ShieldCheck className="h-3 w-3 text-emerald-700" />
              Secure checkout · Stripe
            </div>
            {!user && (
              <div
                data-testid="plus-buy-signin-hint"
                className="mt-2 text-[11px] text-stone-500"
              >
                Sign in required — the report is emailed to you.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
